import { useState } from 'react'
import { useLoadedManta, useRefreshingQuery } from '@happy-ops-org/manta-react'
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { OrcaQueryModel, orcaQueryDef } from './manta.js'
import { OrcaQueryForm } from './OrcaQueryForm.js'
import { OrcaQueryImportPage } from './OrcaQueryImportPage.js'
import type { OrcaQueryDefinition } from './types.js'

/**
 * Editor for the mcp_orca_query table. A user creates named tag-queries, enters
 * the AND tag set, triggers a resolve against the live Orca schema (done by the
 * MCP server), then documents the resulting fields and observed tags. Documents
 * are referenced by their numeric id (no lid).
 */
export const OrcaQueryEditor = () => {
  const manta = useLoadedManta()
  const { state, data } = useRefreshingQuery(
    OrcaQueryModel,
    {},
    { debounceMs: 200 },
  )
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [importing, setImporting] = useState(false)

  if (state === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    )
  }

  const selectedEntry = data?.find((d) => d.id === selectedId)

  const addNew = async () => {
    // Create immediately with a default name; the user renames it in the form
    // (window.prompt is not available in the embedded UI).
    const id = await manta.create(orcaQueryDef, {
      name: 'Neue Abfrage',
      description: '',
      tags: [],
      fields: {},
      observedTags: {},
    })
    setImporting(false)
    setSelectedId(id)
  }

  const selectQuery = (id: number) => {
    setImporting(false)
    setSelectedId(id)
  }

  const save = async (
    id: number,
    draft: OrcaQueryDefinition,
    requestRefresh: boolean,
  ) => {
    await manta.cmd(orcaQueryDef, id, async (mut) => {
      mut.data.name = draft.name
      mut.data.description = draft.description
      mut.data.tags = draft.tags

      // Keep the server-owned generated types + field/tag membership; apply the
      // user's annotations on top so a concurrent reconciler write is not lost.
      const fields = { ...mut.data.fields }
      for (const [fname, sf] of Object.entries(fields)) {
        const d = draft.fields[fname]
        if (d) {
          fields[fname] = {
            type: sf.type,
            description: d.description,
            ...(d.unit ? { unit: d.unit } : {}),
            ...(d.examples?.length ? { examples: d.examples } : {}),
            ...(d.relatesTo ? { relatesTo: d.relatesTo } : {}),
          }
        }
      }
      for (const [fname, d] of Object.entries(draft.fields)) {
        if (!fields[fname]) fields[fname] = d
      }
      mut.data.fields = fields

      const observedTags = { ...mut.data.observedTags }
      for (const [tag, d] of Object.entries(draft.observedTags)) {
        observedTags[tag] = { description: d.description }
      }
      mut.data.observedTags = observedTags

      if (requestRefresh) mut.data.refreshRequestedAt = Date.now()
      return true
    })
  }

  const handleDownload = () => {
    const definitions = Object.fromEntries(
      (data ? Array.from(data) : []).map((e) => [e.data.name, e.data]),
    )
    const blob = new Blob([JSON.stringify(definitions, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'mcp-orca-queries.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const archiveQuery = async (id: number) => {
    await manta.archive(orcaQueryDef, id, true)
    if (selectedId === id) setSelectedId(null)
  }

  const undocumentedCount = (def: OrcaQueryDefinition): number =>
    Object.values(def.fields ?? {}).filter((f) => !f.description.trim()).length

  return (
    <Box display="flex" height="100%" overflow="hidden">
      {/* Sidebar */}
      <Box
        width={280}
        borderRight="1px solid"
        borderColor="divider"
        display="flex"
        flexDirection="column"
        bgcolor="grey.50"
      >
        <Box
          px={2}
          py={1.5}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid"
          borderColor="divider"
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Abfragen
          </Typography>
          <Box display="flex" gap={0.5}>
            <Tooltip title="Alle exportieren">
              <Button
                size="small"
                onClick={handleDownload}
                sx={{ minWidth: 0, px: 1 }}
              >
                <DownloadIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="JSON importieren">
              <Button
                size="small"
                onClick={() => {
                  setSelectedId(null)
                  setImporting(true)
                }}
                sx={{ minWidth: 0, px: 1 }}
                color={importing ? 'primary' : 'inherit'}
              >
                <UploadIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Button size="small" startIcon={<AddIcon />} onClick={addNew}>
              Neu
            </Button>
          </Box>
        </Box>

        <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
          {(data?.size ?? 0) === 0 && (
            <Box p={2}>
              <Typography variant="body2" color="text.disabled">
                Noch keine Abfragen. „Neu" klicken, Tags eingeben und Schema
                laden.
              </Typography>
            </Box>
          )}
          {data?.map((entry) => {
            const undoc = undocumentedCount(entry.data)
            return (
              <Box key={entry.id}>
                <ListItemButton
                  selected={selectedId === entry.id}
                  onClick={() => selectQuery(entry.id)}
                >
                  <ListItemText
                    primary={entry.data.name || '(ohne Namen)'}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                    secondary={
                      entry.data.description
                        ? entry.data.description.slice(0, 50) +
                          (entry.data.description.length > 50 ? '…' : '')
                        : `${entry.data.tags.length} Tag(s)`
                    }
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                  {entry.data.resolveError && (
                    <Tooltip title="Auflösung fehlgeschlagen">
                      <ErrorOutlineIcon
                        color="error"
                        sx={{ fontSize: 16, mr: 0.5 }}
                      />
                    </Tooltip>
                  )}
                  {undoc > 0 && (
                    <Chip
                      label={undoc}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </ListItemButton>
                <Divider />
              </Box>
            )
          })}
        </List>
      </Box>

      {/* Main Area */}
      <Box flex={1} overflow="auto">
        {importing ? (
          <OrcaQueryImportPage />
        ) : selectedEntry ? (
          <OrcaQueryForm
            key={selectedEntry.id}
            initial={selectedEntry.data}
            onSave={(draft, requestRefresh) =>
              save(selectedEntry.id, draft, requestRefresh)
            }
            onArchive={() => archiveQuery(selectedEntry.id)}
          />
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            color="text.disabled"
          >
            <Typography>
              Abfrage wählen, neu anlegen oder JSON importieren.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
